import { ConnectionBlockedError, ConnectionTransientError } from "./model.ts";

/**
 * A Connect refusal as a bridge reports it: thrown as a `ConnectError`, or carried as plain data
 * across a boundary (Desktop IPC) that keeps only an error's message.
 */
const refusalOf = (cause: unknown) =>
  typeof cause === "object" &&
  cause !== null &&
  "code" in cause &&
  typeof cause.code === "string" &&
  "status" in cause &&
  typeof cause.status === "number"
    ? { code: cause.code, status: cause.status }
    : undefined;

/**
 * What a failed Cloud Workspace introduction means for the connection.
 *
 * Only a refusal of this client's account (a 401) is a sign-in problem; a 403 is about what the
 * account may do, and sign-in would send the user to fix the wrong thing. A transport failure, a timeout or
 * a 5xx says nothing about the client: an asleep laptop, a dropped network or a run between
 * hostnames all look like that, and they must retry on their own rather than strand a tab until
 * reload (#1100). A stopped or failed workspace says how to start it; a run still starting is
 * retried until it is ready.
 */
export function cloudWorkspaceConnectionFailure(label: string, cause: unknown) {
  const refusal = refusalOf(cause);
  if (refusal === undefined) {
    return new ConnectionBlockedError({
      reason: "credential",
      detail: `${label} could not be reached through AFAR Connect: ${String(cause)}`,
    });
  }
  switch (refusal.code) {
    case "guest_stopped":
      return new ConnectionBlockedError({
        reason: "configuration",
        detail: `${label} is stopped. Start it from its parent's Guest Environments settings to connect.`,
      });
    case "guest_starting":
      return new ConnectionTransientError({
        reason: "environment-starting",
        detail: `${label} and its parent are starting. It connects once they are ready.`,
      });
    case "workspace_stopped":
      return new ConnectionBlockedError({
        reason: "configuration",
        detail: `${label} is stopped. Start it from Settings → Connections → Cloud Workspaces to connect.`,
      });
    case "workspace_failed":
      return new ConnectionBlockedError({
        reason: "configuration",
        detail: `${label} could not start. Start it again from Settings → Connections to connect.`,
      });
    case "insufficient_credits":
      return new ConnectionBlockedError({
        reason: "configuration",
        detail: `${label} cannot start because its account has no Usage Allowance left.`,
      });
    case "workspace_starting":
      return new ConnectionTransientError({
        reason: "environment-starting",
        detail: `${label} is starting and connects once its run is ready.`,
      });
  }
  if (refusal.status === 401) {
    return new ConnectionBlockedError({
      reason: "credential",
      detail: `AFAR Connect did not accept this client's sign-in for ${label} (${refusal.code}).`,
    });
  }
  if (refusal.status >= 500 || refusal.status === 408 || refusal.status === 429) {
    // Not `endpoint-unavailable`: that reads as a run still starting, and this is no such news.
    return new ConnectionTransientError({
      reason: "host-unavailable",
      detail: `${label} could not be reached through AFAR Connect right now (${refusal.code}).`,
    });
  }
  return new ConnectionBlockedError({
    reason: "configuration",
    detail: `${label} could not be reached through AFAR Connect (${refusal.code}).`,
  });
}
